import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_CUSTOM_TIME_VALUE,
  DEFAULT_SCHEDULE_TIME,
  formatLocalTime,
  localTimeToRepeatRule,
  parseTimeInputValue,
} from '../lib/schedule';

// ─────────────────────────────────────────────────────────────────────────────
// SendScheduleMenu — the ▾ half of the composer's split Send button (T-0156).
//
// The Gmail schedule-send gesture: a repeat rule is a *modifier on the Request you
// already typed*, never a page, a tab, or a config object you go configure
// elsewhere (rnd/SCHEDULED_REQUESTS_DECISION.md). So this is two taps over sending
// once — ▾, then the cadence — and the composer's own Send button becomes the
// third tap it always was.
//
// "Tomorrow 8:00" is deliberately GRAYED. The backend understands exactly one rule,
// `daily@HH:MM` (RepeatRule::parse), so a one-off would have to be faked client-side
// — arm a daily rule and delete it after the first fire. That fake breaks the moment
// the browser is closed, and it makes the space sheet's row lie about what it is. It
// stays honest and disabled until the server grows one-shot recurrence.
//
// At ≤768px the popover becomes a bottom sheet (styles.css), the mobile-P1 pattern:
// anchored above the tab bar, over a scrim, thumb-reachable at 390px.
// ─────────────────────────────────────────────────────────────────────────────

const ONE_OFF_UNAVAILABLE_NOTE = 'Coming soon — Schedules repeat daily for now';

export interface SendScheduleMenuProps {
  /** Mirrors the Send button's own disabled state (sending, empty, space asleep). */
  disabled: boolean;
  /** Why the ▾ is unavailable, when the reason isn't obvious from the composer. */
  disabledReason?: string;
  /** The plain "Send now" item — sends the Request once, exactly as Send does. */
  onSendNow: () => void;
  /** A cadence was chosen: a `daily@HH:MM` UTC rule for the composer to confirm. */
  onPickRule: (rule: string) => void;
  /** The composer's own submit button — it forms the left half of the split, so it
   *  lives inside this container rather than beside it. */
  children: ReactNode;
}

export function SendScheduleMenu({
  disabled,
  disabledReason,
  onSendNow,
  onPickRule,
  children,
}: SendScheduleMenuProps) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState(DEFAULT_CUSTOM_TIME_VALUE);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const customInputId = useId();

  const dailyPresetLabel = `Daily ${formatLocalTime(DEFAULT_SCHEDULE_TIME)}`;
  const oneOffLabel = `Tomorrow ${formatLocalTime(DEFAULT_SCHEDULE_TIME)}`;

  // Close on Esc, and on a press outside the split button. Both `pointerdown` and
  // `click` are handled: some environments (Playwright-Firefox touch emulation)
  // deliver a bare click with no pointer events, and closing twice is a no-op.
  useEffect(() => {
    if (!open && !customOpen) return;
    function dismiss(): void {
      setOpen(false);
      setCustomOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      dismiss();
      toggleRef.current?.focus();
    }
    function onOutside(e: Event): void {
      if (!rootRef.current?.contains(e.target as Node)) dismiss();
    }
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('click', onOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onOutside);
      document.removeEventListener('click', onOutside);
    };
  }, [open, customOpen]);

  // The composer's Send goes disabled the instant the draft empties or the space
  // sleeps; an open menu over a Send you can no longer press is a dead end.
  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
    setCustomOpen(false);
  }, [disabled]);

  function closeAll(): void {
    setOpen(false);
    setCustomOpen(false);
  }

  function pick(local: { hour: number; minute: number }): void {
    closeAll();
    onPickRule(localTimeToRepeatRule(local));
  }

  function handleSendNow(): void {
    closeAll();
    onSendNow();
  }

  function handleCustomSubmit(): void {
    const local = parseTimeInputValue(customValue);
    if (!local) return;
    pick(local);
  }

  return (
    <div className="composer-send-split" ref={rootRef}>
      {children}
      <button
        type="button"
        ref={toggleRef}
        className="composer-send-more"
        // "Schedule options" rather than anything containing "send": the composer's
        // Send button is addressed by name across the unit + e2e suites, and a second
        // /send/i match would make every one of those queries ambiguous.
        aria-label="Schedule options"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => {
          setCustomOpen(false);
          setOpen((v) => !v);
        }}
      >
        <span aria-hidden="true">▾</span>
      </button>

      {(open || customOpen) && (
        // Scrim: invisible on desktop (the document listener above is what closes
        // the popover there), the sheet's dimmed backdrop on a phone.
        <div
          className="composer-send-scrim"
          data-testid="composer-send-scrim"
          onPointerDown={closeAll}
          onClick={closeAll}
        />
      )}

      {open && (
        <div className="composer-send-menu" role="menu" aria-label="Send options">
          <button type="button" role="menuitem" className="composer-send-menu-item" onClick={handleSendNow}>
            Send now
          </button>
          <button
            type="button"
            role="menuitem"
            className="composer-send-menu-item"
            disabled
            aria-disabled="true"
            title={ONE_OFF_UNAVAILABLE_NOTE}
          >
            <span>{oneOffLabel}</span>
            <span className="composer-send-menu-note">{ONE_OFF_UNAVAILABLE_NOTE}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="composer-send-menu-item"
            onClick={() => pick(DEFAULT_SCHEDULE_TIME)}
          >
            {dailyPresetLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            className="composer-send-menu-item"
            onClick={() => {
              setOpen(false);
              setCustomOpen(true);
            }}
          >
            Custom…
          </button>
        </div>
      )}

      {customOpen && (
        // A time field, not a rule field. The user picks the hour they want it in
        // their own zone; the UTC rule is computed on the way out.
        <div className="composer-send-custom" role="group" aria-label="Custom schedule">
          <label htmlFor={customInputId} className="composer-send-custom-label">
            Repeat every day at
          </label>
          <input
            id={customInputId}
            type="time"
            className="composer-send-custom-input"
            value={customValue}
            autoFocus
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              handleCustomSubmit();
            }}
          />
          <button
            type="button"
            className="composer-send-custom-set btn-secondary"
            disabled={!parseTimeInputValue(customValue)}
            onClick={handleCustomSubmit}
          >
            Set time
          </button>
        </div>
      )}
    </div>
  );
}
